import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from "@pages/PageStructure.component";
import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DraftOnly } from "@components/project/DraftModeRequired.component";
import PipelineNodeList, { NodeGrid } from "@components/pipelineNodes/PipelineNodeList.component";
import { savePipelineNode, usePipelineNodes } from "@stores/data.store";
import { getGroupValueForNodeType } from "@services/modeling.service";
import ObjectListHeader from "@components/common/ObjectListHeader.component";
import { DashboardIcon } from "@components/pipelineNodes/PipelineNodeIcon.component";
import toast from "@services/toast.service";
import { getErrorMessage } from "@services/errors.service";
import NewDatasetModal from "./NewDatasetModal.component";
import { getPromptAnswer } from "@services/alert/alert.service";

const DatasetsListPage = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [showNewDatasetModal, setShowNewDatasetModal] = useState(false);

    const handleCreateDataset = useCallback(async (type: 'semantic' | 'datamart', name?: string) => {
        setShowNewDatasetModal(false);

        if (type === 'semantic') {
            navigate('/exports/dataset-wizard');
        } else if (type === 'datamart') {
            try {
                const newNode = await savePipelineNode({
                    id: null,
                    node_type: 'DATAMART',
                    name: name || 'New Dataset',
                    label: name || 'New Dataset',
                    description: '',
                    upstream_node_ids: [],
                    fields: [],
                    table_name: '',
                });
                navigate(`/node/${newNode.id}/config`);
            } catch (err) {
                toast('danger', 'Error', getErrorMessage(err));
            }
        }
    }, [navigate]);

    const newDestination = useCallback(async () => {
        const name = await getPromptAnswer('Enter destination name', 'New Destination');
        if (name) {
            const newNode = await savePipelineNode({
                id: null,
                node_type: 'DESTINATION',
                name: name,
                label: name,
                description: '',
                upstream_node_ids: [],
                fields: [],
                table_name: '',
            });
            navigate(`/node/${newNode.id}/config`);
        }
    }, [])
    return (
        <PageStructure>
            <NewDatasetModal
                show={showNewDatasetModal}
                onClose={() => setShowNewDatasetModal(false)}
                onCreateDataset={handleCreateDataset}
            />
            <PageContent>
                <PageContentHeader>
                    <div className="d-flex center-vertically">
                        <div style={{width:'40%'}}>
                            <h1 className="mb-0">Exports</h1>
                            <div className="text-muted font-13">Manage data exported from Pliable to other tools.</div>
                        </div>
                        <div className="flex-1 ps-5 pe-5">
                            <input 
                                type="text" 
                                className="form-control input-rounded input-search" 
                                placeholder="Search..." 
                                value={search} 
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </PageContentHeader>
                <PageContentInner hasHeader>
                    <Pane>
                        <PaneContent>
                            <div className="p-4">
                               
                                <PipelineNodeList
                                    headerIcon="mdi mdi-table-check"
                                    headerIconColor="secondary"
                                    title="Datasets"
                                    actionButtonText="New Dataset"
                                    actionButtonIcon="mdi mdi-plus-thick"
                                    actionButtonOnClick={() => setShowNewDatasetModal(true)}
                                    headerChildren={<div className="text-muted font-13">Datasets allow you to expose data from Pliable to other data tools via Snowflake.</div>}
                                    searchQuery={search}
                                    nodeFilter={(node) => ['DATAMART', 'REPORT'].includes(node.node_type)}
                                />
                                <PipelineNodeList
                                    headerIcon="mdi mdi-sync-circle"
                                    headerIconColor="secondary"
                                    title="Destinations"
                                    actionButtonText="New Destination"
                                    actionButtonIcon="mdi mdi-plus-thick"
                                    actionButtonOnClick={newDestination}
                                    headerChildren={<div className="text-muted font-13">Destinations allow you to model data from datasets and entities so that they can be imported into your operational systems.</div>}
                                    searchQuery={search}
                                    nodeFilter={(node) => ['DESTINATION'].includes(node.node_type)}
                                />
                                
                            </div>
                        </PaneContent>
                    </Pane>
                </PageContentInner>
            </PageContent>
        </PageStructure>
    );
}

export default DatasetsListPage;
